import { Menu } from '@base-ui/react/menu'
import { useRouter } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, LogOut, Settings } from 'lucide-react'
import { apiFetch } from '@/api/client'
import { useAuth } from '@/app/auth-context'
import type { Organization } from '@/domain'
// ponytail: the shell borrows the Settings page's avatar + profile query rather
// than duplicating them. Same PROFILE_QUERY_KEY, so react-query dedupes the
// request and the optimistic PATCH from the avatar picker repaints this chip
// with no extra state. Move both out of pages/ if a third consumer shows up.
import { ProfileAvatar } from '@/pages/settings/avatar'
import { useProfile } from '@/pages/settings/use-profile'
import { orgRoleLabel } from './org-labels'
import { GROUP_LABEL_STYLE, MENU_ITEM_STYLE, POPUP_STYLE } from './popup-style'

const ORG_KEY = 'it.orgId'

function readStoredOrgId(): string | null {
  try {
    return sessionStorage.getItem(ORG_KEY)
  } catch {
    return null
  }
}

/** Avatar + name trigger; menu shows the signed-in identity, role, and Sign out. */
export function UserMenu() {
  const { session, signOut } = useAuth()
  const router = useRouter()
  const { data: profile } = useProfile()
  const display = profile?.name ?? session?.name ?? session?.email ?? 'User'

  // The current org's role for this user — backend truth is
  // organization_users.role (admin|audit|member), surfaced via /orgs.
  const { data } = useQuery({
    queryKey: ['orgs'],
    queryFn: () => apiFetch<Organization[]>({ url: '/api/v1/orgs' }),
    staleTime: 5 * 60_000,
    retry: false,
  })
  const orgs = Array.isArray(data) ? data : []
  const activeOrgId = readStoredOrgId() ?? session?.orgId ?? null
  const activeOrg = orgs.find((o) => o.id === activeOrgId) ?? orgs[0]
  const roleLabel = activeOrg ? orgRoleLabel(activeOrg.role) : ''

  return (
    <Menu.Root>
      <Menu.Trigger
        className="it-icon-btn it-shell-btn"
        aria-label={`User menu — ${display}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          height: 32,
          width: 'auto',
          padding: '0 8px 0 4px',
          borderRadius: 'var(--r-sm)',
          border: 0,
          background: 'transparent',
          color: 'var(--text)',
          cursor: 'pointer',
          font: 'inherit',
          fontSize: 13,
        }}
      >
        {/* avatarIcon/avatarColor already fall back to the first option, so a
            pending profile renders the same default glyph the API serves. */}
        <ProfileAvatar
          iconId={profile?.iconId ?? ''}
          iconColor={profile?.iconColor ?? ''}
          size={26}
        />
        <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {display}
        </span>
        <ChevronDown size={12} color="var(--text-faint)" aria-hidden />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner side="bottom" align="end" sideOffset={6} style={{ zIndex: 60 }}>
          <Menu.Popup style={POPUP_STYLE}>
            {/* Menu.GroupLabel throws outside a Menu.Group (Base UI 1.5). */}
            <Menu.Group>
              <Menu.GroupLabel style={GROUP_LABEL_STYLE}>Signed in as</Menu.GroupLabel>
              <div
                style={{
                  padding: '0 10px 8px',
                  borderBottom: '1px solid var(--border)',
                  marginBottom: 4,
                }}
              >
                <div
                  style={{ fontSize: 12, color: 'var(--text-soft)', wordBreak: 'break-all' }}
                >
                  {session?.email ?? '—'}
                </div>
                {roleLabel && activeOrg && (
                  <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 2 }}>
                    {roleLabel} · {activeOrg.name}
                  </div>
                )}
              </div>
            </Menu.Group>
            <Menu.Item
              className="it-menu-item"
              style={MENU_ITEM_STYLE}
              onClick={() => router.history.push('/settings')}
            >
              <Settings size={14} color="var(--text-mute)" aria-hidden />
              Settings
            </Menu.Item>
            <Menu.Item
              className="it-menu-item"
              style={MENU_ITEM_STYLE}
              onClick={() => signOut()}
            >
              <LogOut size={14} color="var(--text-mute)" aria-hidden />
              Sign out
            </Menu.Item>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  )
}
